import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiDollarSign, FiUsers, FiShoppingBag, FiRefreshCw, FiShoppingCart, FiEye, FiChevronRight } from 'react-icons/fi';
import { supabase } from '../../config/supabase';
import { useAuthContext } from '../../hooks/useAuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { format, startOfDay, startOfWeek, startOfMonth, startOfYear } from 'date-fns';
import { formatOrderReference } from '../../utils/orderReference';
import { sumLineTotals } from '../../utils/sellerOrderTotals';

const DashboardContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;

  @media (max-width: 768px) {
    gap: 20px;
    overflow-x: hidden;
  }
`;

const FilterBar = styled.div`
  display: flex;
  justify-content: flex-start;
  width: 100%;
  overflow-x: auto;
  direction: ltr; /* Keep filter bar LTR for consistent layout */

  @media (max-width: 768px) {
    margin: 0;
    padding: 0;
  }
`;

const FilterButtons = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: nowrap;
  direction: ltr; /* Keep buttons LTR */

  @media (max-width: 768px) {
    width: 100%;
    overflow-x: auto;
    padding-bottom: 4px;
  }
`;

const FilterButton = styled.button`
  padding: 8px 16px;
  border: 1px solid ${props => props.$active ? '#5efba9' : '#e5e7eb'};
  border-radius: 8px;
  background: ${props => props.$active ? '#5efba9' : 'white'};
  font-size: 14px;
  font-weight: ${props => props.$active ? '600' : '400'};
  color: #1f2937;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    background: ${props => props.$active ? '#4de89e' : '#f9fafb'};
    border-color: ${props => props.$active ? '#4de89e' : '#d1d5db'};
  }

  @media (max-width: 768px) {
    padding: 8px 12px;
    font-size: 13px;
  }
`;

const KPICards = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  direction: ltr; /* Keep grid LTR */

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const KPICard = styled.div`
  background: ${props => props.variant === 'dark' ? '#1f2937' : '#5efba9'};
  color: ${props => props.variant === 'dark' ? 'white' : '#1f2937'};
  padding: 22px;
  overflow: hidden;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  direction: ${props => props.$isArabic ? 'rtl' : 'ltr'};
  text-align: ${props => props.$isArabic ? 'right' : 'left'};

  @media (max-width: 768px) {
    padding: 18px;
  }
`;

const KPIHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const KPIIcon = styled.div`
  width: 38px;
  height: 38px;
  background: ${props => props.variant === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
`;

const KPILabel = styled.div`
  font-size: 13px;
  opacity: 0.8;
`;

const KPIValue = styled.div`
  font-size: 30px;
  font-weight: 700;
  opacity: ${props => props.$loading ? 0.5 : 1};
  transition: opacity 0.2s;
  word-break: break-word;
  line-height: 1.15;
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    font-size: 24px;
  }
  
  @media (max-width: 400px) {
    font-size: 20px;
  }

  .kpiCurrency {
    display: inline-block;
    font-size: 12px;
    font-weight: 700;
    opacity: 0.85;
    margin-right: 8px;
    vertical-align: baseline;

    @media (max-width: 768px) {
      font-size: 11px;
      margin-right: 6px;
    }
  }
`;

const KPITrend = styled.div`
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const OrdersSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  max-width: 100%;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 16px;
    padding-right: 0;
    border-radius: 8px;
    overflow-x: visible;
  }
`;

const OrdersHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  gap: 12px;
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`;

const ViewAllLink = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: #5efba9;
  color: #1f2937;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: #4de89e;
  }
  
  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 13px;
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  max-width: 100%;
  
  @media (max-width: 768px) {
    margin: 0 -16px;
    padding: 0;
    
    &::-webkit-scrollbar {
      height: 4px;
    }
    
    &::-webkit-scrollbar-thumb {
      background: #d1d5db;
      border-radius: 2px;
    }
  }
`;

const OrdersTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  
  @media (max-width: 768px) {
    min-width: 600px;
  }
`;

const OrdersTableHeader = styled.thead`
  background: #f9fafb;
`;

const OrdersTableHeaderRow = styled.tr`
  border-bottom: 1px solid #e5e7eb;
`;

const OrdersTableHeaderCell = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-size: 14px;
  font-weight: 600;
  color: #6b7280;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    padding: 10px 12px;
    font-size: 12px;
  }
`;

const OrdersTableBody = styled.tbody``;

const OrdersTableRow = styled.tr`
  border-bottom: 1px solid #f3f4f6;
  transition: background 0.2s;
  cursor: pointer;

  &:hover {
    background: #f9fafb;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const OrdersTableCell = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  color: #1f2937;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    padding: 10px 12px;
    font-size: 12px;
  }
`;

const OrderStatusBadge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => {
    if (props.status === 'completed') return '#d1fae5';
    if (props.status === 'pending') return '#fef3c7';
    if (props.status === 'processing' || props.status === 'confirmed') return '#dbeafe';
    if (props.status === 'canceled') return '#fee2e2';
    return '#e5e7eb';
  }};
  color: ${props => {
    if (props.status === 'completed') return '#065f46';
    if (props.status === 'pending') return '#92400e';
    if (props.status === 'processing' || props.status === 'confirmed') return '#1e40af';
    if (props.status === 'canceled') return '#991b1b';
    return '#374151';
  }};
`;

const EmptyOrders = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #6b7280;
`;

export default function AdminDashboard() {
  const { session } = useAuthContext();
  const { isArabic } = useLanguage();
  const navigate = useNavigate();
  const [timeFilter, setTimeFilter] = useState('all'); // 'daily', 'weekly', 'monthly', 'yearly', 'all'
  const [dashboardData, setDashboardData] = useState({
    totalSales: 0,
    orderCount: 0,
    customerCount: 0,
    refundCount: 0,
    loading: true
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);

  // Get the start date based on the filter
  const getFilterStartDate = () => {
    const now = new Date();
    switch (timeFilter) {
      case 'daily':
        return startOfDay(now).toISOString();
      case 'weekly':
        return startOfWeek(now, { weekStartsOn: 0 }).toISOString();
      case 'monthly':
        return startOfMonth(now).toISOString();
      case 'yearly':
        return startOfYear(now).toISOString();
      default:
        return null; // 'all' - no date filter
    }
  };

  useEffect(() => {
    if (session) {
      fetchDashboardData();
      fetchRecentOrders();
    }
  }, [session, timeFilter]);

  const fetchDashboardData = async () => {
    if (!session) return;

    try {
      setDashboardData(prev => ({ ...prev, loading: true }));
      
      // Get the user's brand_id
      let brandId = null;
      
      const { data: brandManager, error: brandError } = await supabase
        .from('brand_managers')
        .select('brand_id')
        .eq('profile_id', session.user.id)
        .maybeSingle();

      if (brandManager && brandManager.brand_id) {
        brandId = brandManager.brand_id;
      } else {
        // Fallback: Try to find brand by created_by (use most recent if multiple)
        const { data: userBrands, error: brandsError } = await supabase
          .from('brands')
          .select('id')
          .eq('created_by', session.user.id)
          .order('created_at', { ascending: false })
          .limit(1);

        if (!brandsError && userBrands && userBrands.length > 0) {
          brandId = userBrands[0].id;
          if (userBrands.length > 1) {
            console.warn('⚠️ User has multiple brands. Using the most recent one.');
          }
        }
      }

      if (!brandId) {
        console.error('Error: No brand found for user');
        setDashboardData(prev => ({ ...prev, loading: false }));
        return;
      }

      // Build query for order items (only paid orders)
      let query = supabase
        .from('order_items')
        .select(`
          line_total,
          created_at,
          orders!inner(
            id,
            profile_id,
            status,
            payment_status,
            created_at
          )
        `)
        .eq('brand_id', brandId)
        .eq('orders.payment_status', 'paid');

      // Apply date filter
      const startDate = getFilterStartDate();
      if (startDate) {
        query = query.gte('orders.created_at', startDate);
      }

      const { data: orderItems, error: itemsError } = await query;

      if (itemsError) {
        console.error('Error fetching dashboard data:', itemsError);
        setDashboardData(prev => ({ ...prev, loading: false }));
        return;
      }

      // Calculate metrics
      // NOTE: totalSales uses line_total from order_items which is (unit_price × quantity)
      // This correctly EXCLUDES shipping fees, payment fees, and other order-level charges
      // Shipping fees belong to the platform/shipping company, not the brand
      const totalSales = orderItems.reduce((sum, item) => sum + parseFloat(item.line_total || 0), 0);
      const uniqueOrderIds = [...new Set(orderItems.map(item => item.orders.id))];
      const orderCount = uniqueOrderIds.length;

      // Get unique customers
      const uniqueCustomers = [...new Set(orderItems.map(item => item.orders.profile_id))];
      const customerCount = uniqueCustomers.length;

      // Count refunded orders separately (fetch all order IDs for this brand, then count refunded ones)
      // Get all order IDs that have items from this brand (regardless of payment status)
      const { data: allBrandOrderItems, error: allItemsError } = await supabase
        .from('order_items')
        .select('order_id, orders!inner(id, payment_status, created_at)')
        .eq('brand_id', brandId);

      let refundCount = 0;
      if (!allItemsError && allBrandOrderItems) {
        // Apply date filter if needed
        let filteredItems = allBrandOrderItems;
        if (startDate) {
          filteredItems = allBrandOrderItems.filter(item => 
            new Date(item.orders.created_at) >= new Date(startDate)
          );
        }

        // Get unique order IDs that are refunded
        const refundedOrderIds = [...new Set(
          filteredItems
            .filter(item => item.orders.payment_status === 'refunded')
            .map(item => item.orders.id)
        )];
        refundCount = refundedOrderIds.length;
      }

      setDashboardData({
        totalSales,
        orderCount,
        customerCount,
        refundCount,
        loading: false
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setDashboardData(prev => ({ ...prev, loading: false }));
    }
  };

  const fetchRecentOrders = async () => {
    if (!session) return;

    try {
      setOrdersLoading(true);

      // Get the user's brand_id
      const { data: brandManager, error: brandError } = await supabase
        .from('brand_managers')
        .select('brand_id')
        .eq('profile_id', session.user.id)
        .single();

      if (brandError || !brandManager) {
        console.error('Error fetching brand manager:', brandError);
        setRecentOrders([]);
        setOrdersLoading(false);
        return;
      }

      // Build query for order items (only paid orders)
      let itemsQuery = supabase
        .from('order_items')
        .select('order_id, line_total, orders!inner(created_at, payment_status)')
        .eq('brand_id', brandManager.brand_id)
        .eq('orders.payment_status', 'paid');
      
      // Apply date filter
      const startDate = getFilterStartDate();
      if (startDate) {
        itemsQuery = itemsQuery.gte('orders.created_at', startDate);
      }
      
      const { data: orderItems, error: itemsError } = await itemsQuery.limit(100);

      if (itemsError) {
        console.error('Error fetching order items:', itemsError);
        setRecentOrders([]);
        setOrdersLoading(false);
        return;
      }

      if (!orderItems || orderItems.length === 0) {
        setRecentOrders([]);
        setOrdersLoading(false);
        return;
      }

      // Get unique order IDs and seller subtotals per order
      const sellerSubtotalByOrderId = new Map();
      orderItems.forEach((item) => {
        const current = sellerSubtotalByOrderId.get(item.order_id) || 0;
        sellerSubtotalByOrderId.set(item.order_id, current + parseFloat(item.line_total || 0));
      });
      const orderIds = [...sellerSubtotalByOrderId.keys()];

      // Build query for orders (only paid orders)
      let ordersQuery = supabase
        .from('orders')
        .select(`
          id,
          order_number,
          created_at,
          currency,
          status,
          payment_status,
          profile_id
        `)
        .in('id', orderIds)
        .eq('payment_status', 'paid')
        .order('created_at', { ascending: false });

      // Apply date filter again on orders directly
      if (startDate) {
        ordersQuery = ordersQuery.gte('created_at', startDate);
      }

      const { data: ordersData, error: ordersError } = await ordersQuery.limit(10);

      if (ordersError) {
        console.error('Error fetching orders:', ordersError);
        setRecentOrders([]);
      } else {
        setRecentOrders((ordersData || []).map((order) => ({
          ...order,
          sellerSubtotal: sellerSubtotalByOrderId.get(order.id) || 0,
        })));
      }
    } catch (error) {
      console.error('Error fetching recent orders:', error);
      setRecentOrders([]);
    } finally {
      setOrdersLoading(false);
    }
  };

  const handleViewOrder = (orderId) => {
    navigate('/admin/orders');
    // Optionally scroll to or highlight the order in the orders page
  };

  // Get section title based on filter
  const getOrdersSectionTitle = () => {
    switch (timeFilter) {
      case 'daily':
        return isArabic ? 'طلبات اليوم' : "Today's Orders";
      case 'weekly':
        return isArabic ? 'طلبات هذا الأسبوع' : "This Week's Orders";
      case 'monthly':
        return isArabic ? 'طلبات هذا الشهر' : "This Month's Orders";
      case 'yearly':
        return isArabic ? 'طلبات هذا العام' : "This Year's Orders";
      default:
        return isArabic ? 'الطلبات الأخيرة' : 'Recent Orders';
    }
  };

  return (
    <DashboardContainer>
      <FilterBar>
        <FilterButtons>
          <FilterButton 
            $active={timeFilter === 'daily'} 
            onClick={() => setTimeFilter('daily')}
          >
            {isArabic ? 'يومي' : 'Daily'}
          </FilterButton>
          <FilterButton 
            $active={timeFilter === 'weekly'} 
            onClick={() => setTimeFilter('weekly')}
          >
            {isArabic ? 'أسبوعي' : 'Weekly'}
          </FilterButton>
          <FilterButton 
            $active={timeFilter === 'monthly'} 
            onClick={() => setTimeFilter('monthly')}
          >
            {isArabic ? 'شهري' : 'Monthly'}
          </FilterButton>
          <FilterButton 
            $active={timeFilter === 'yearly'} 
            onClick={() => setTimeFilter('yearly')}
          >
            {isArabic ? 'سنوي' : 'Yearly'}
          </FilterButton>
          <FilterButton 
            $active={timeFilter === 'all'} 
            onClick={() => setTimeFilter('all')}
          >
            {isArabic ? 'الكل' : 'All Time'}
          </FilterButton>
        </FilterButtons>
      </FilterBar>

      <KPICards>
        <KPICard $isArabic={isArabic}>
          <KPIHeader>
            <KPIIcon variant="light">
              <FiDollarSign />
            </KPIIcon>
            <KPILabel>{isArabic ? 'إجمالي المبيعات' : 'Total Sales'}</KPILabel>
          </KPIHeader>
          <KPIValue $loading={dashboardData.loading}>
            <span className="kpiCurrency">{isArabic ? 'ريال عماني' : 'OMR'}</span>
            {dashboardData.totalSales.toFixed(2)}
          </KPIValue>
        </KPICard>

        <KPICard variant="dark" $isArabic={isArabic}>
          <KPIHeader>
            <KPIIcon variant="dark">
              <FiUsers />
            </KPIIcon>
            <KPILabel>{isArabic ? 'العملاء' : 'Customers'}</KPILabel>
          </KPIHeader>
          <KPIValue $loading={dashboardData.loading}>{dashboardData.customerCount}</KPIValue>
        </KPICard>

        <KPICard $isArabic={isArabic}>
          <KPIHeader>
            <KPIIcon variant="light">
              <FiShoppingBag />
            </KPIIcon>
            <KPILabel>{isArabic ? 'إجمالي الطلبات' : 'Total Orders'}</KPILabel>
          </KPIHeader>
          <KPIValue $loading={dashboardData.loading}>{dashboardData.orderCount}</KPIValue>
        </KPICard>

        <KPICard variant="dark" $isArabic={isArabic}>
          <KPIHeader>
            <KPIIcon variant="dark">
              <FiRefreshCw />
            </KPIIcon>
            <KPILabel>{isArabic ? 'إجمالي المستردات' : 'Total Refunded'}</KPILabel>
          </KPIHeader>
          <KPIValue $loading={dashboardData.loading}>{dashboardData.refundCount}</KPIValue>
        </KPICard>
      </KPICards>

      <OrdersSection>
        <OrdersHeader>
          <SectionTitle>{getOrdersSectionTitle()}</SectionTitle>
          <ViewAllLink onClick={() => navigate('/admin/orders')}>
            {isArabic ? 'عرض الكل' : 'View All'} <FiChevronRight />
          </ViewAllLink>
        </OrdersHeader>

        {ordersLoading ? (
          <EmptyOrders>{isArabic ? 'جاري تحميل الطلبات...' : 'Loading orders...'}</EmptyOrders>
        ) : recentOrders.length === 0 ? (
          <EmptyOrders>
            <FiShoppingCart style={{ fontSize: '48px', marginBottom: '12px', opacity: 0.5 }} />
            <p>{isArabic ? 'لم يتم العثور على طلبات' : 'No orders found'}</p>
          </EmptyOrders>
        ) : (
          <TableWrapper>
          <OrdersTable>
            <OrdersTableHeader>
              <OrdersTableHeaderRow>
                <OrdersTableHeaderCell>{isArabic ? 'رقم الطلب' : 'Order #'}</OrdersTableHeaderCell>
                <OrdersTableHeaderCell>{isArabic ? 'التاريخ' : 'Date'}</OrdersTableHeaderCell>
                <OrdersTableHeaderCell>{isArabic ? 'إجمالي البائع' : 'Seller Total'}</OrdersTableHeaderCell>
                <OrdersTableHeaderCell>{isArabic ? 'الحالة' : 'Status'}</OrdersTableHeaderCell>
                <OrdersTableHeaderCell>{isArabic ? 'الدفع' : 'Payment'}</OrdersTableHeaderCell>
                <OrdersTableHeaderCell>{isArabic ? 'الإجراءات' : 'Actions'}</OrdersTableHeaderCell>
              </OrdersTableHeaderRow>
            </OrdersTableHeader>
            <OrdersTableBody>
              {recentOrders.map(order => (
                <OrdersTableRow key={order.id} onClick={() => handleViewOrder(order.id)}>
                  <OrdersTableCell>
                    <code style={{ fontSize: '12px' }}>#{formatOrderReference(order)}</code>
                  </OrdersTableCell>
                  <OrdersTableCell>
                    {order.created_at
                      ? format(new Date(order.created_at), 'MMM dd, yyyy HH:mm')
                      : '-'}
                  </OrdersTableCell>
                  <OrdersTableCell>
                    <strong>
                      {order.currency || (isArabic ? 'ريال عماني' : 'OMR')} {parseFloat(order.sellerSubtotal || 0).toFixed(2)}
                    </strong>
                  </OrdersTableCell>
                  <OrdersTableCell>
                    <OrderStatusBadge status={order.status}>
                      {isArabic ? (
                        order.status === 'pending' ? 'قيد الانتظار' :
                          order.status === 'confirmed' ? 'تم التأكيد' :
                            order.status === 'processing' ? 'قيد المعالجة' :
                              order.status === 'completed' ? 'مكتمل' :
                                order.status === 'canceled' ? 'ملغي' :
                                  order.status
                      ) : (order.status || 'pending')}
                    </OrderStatusBadge>
                  </OrdersTableCell>
                  <OrdersTableCell>
                    <span style={{
                      fontSize: '12px',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      background: order.payment_status === 'paid' ? '#d1fae5' : '#fef3c7',
                      color: order.payment_status === 'paid' ? '#065f46' : '#92400e'
                    }}>
                      {isArabic ? (order.payment_status === 'paid' ? 'مدفوع' : 'غير مدفوع') : (order.payment_status || 'unpaid')}
                    </span>
                  </OrdersTableCell>
                  <OrdersTableCell>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewOrder(order.id);
                      }}
                      style={{
                        background: 'none',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        padding: '6px 12px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '12px',
                        color: '#6b7280',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = '#5efba9';
                        e.target.style.borderColor = '#5efba9';
                        e.target.style.color = '#1f2937';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'none';
                        e.target.style.borderColor = '#e5e7eb';
                        e.target.style.color = '#6b7280';
                      }}
                    >
                      <FiEye /> {isArabic ? 'عرض' : 'View'}
                    </button>
                  </OrdersTableCell>
                </OrdersTableRow>
              ))}
            </OrdersTableBody>
          </OrdersTable>
          </TableWrapper>
        )}
      </OrdersSection>
    </DashboardContainer>
  );
}
